import React, { useState, useEffect, useRef } from 'react';
import { Navbar } from './components/Navbar';
import { Sidebar, NavTab } from './components/Sidebar';
import { CommandCenter } from './components/CommandCenter';
import { Dashboard } from './components/Dashboard';
import { RemoteCareRPM } from './components/RemoteCareRPM';
import { AmbientSafetyDashboard } from './components/AmbientSafetyDashboard';
import { ClinicalDocs } from './components/ClinicalDocs';
import { SeniorLivingSuite } from './components/SeniorLivingSuite';
import { PreventiveWellnessSuite } from './components/PreventiveWellnessSuite';
import { PrintPreviewModal } from './components/PrintPreviewModal';
import { FamilyDigestModal } from './components/FamilyDigestModal';
import { AccessManagementModal } from './components/AccessManagementModal';
import { AiClinicalAssistant } from './components/AiClinicalAssistant';
import {
  INITIAL_PATIENTS,
  INITIAL_RESIDENTS,
  INITIAL_ROOM_SENSORS,
  INITIAL_HOME_RPM_SENIORS,
  INITIAL_EARLY_DETERIORATION_SIGNALS,
  INITIAL_AMBIENT_ROOMS,
  INITIAL_DEPARTMENT_METRICS,
  INITIAL_ALERTS,
  SAMPLE_INITIAL_DOC,
  INITIAL_CLINICAL_BUSINESSES,
  INITIAL_USERS
} from './constants';
import {
  Patient,
  ClinicalDocument,
  OperationalAlert,
  Resident,
  RoomAmbientSensor,
  HomeRPMSenior,
  EarlyDeteriorationSignal,
  FamilyDigestSubject,
  AmbientRoom,
  ClinicalBusiness,
  UserProfile,
  LicenseTier
} from './types';
import { Search, ShieldAlert, KeyRound, Building2, Printer, ShieldCheck, FileText, CheckCircle2 } from 'lucide-react';
import { playFallAlertSound } from './services/audioService';

export const App: React.FC = () => {
  const [currentTab, setCurrentTab] = useState<NavTab>('command');
  const [patients, setPatients] = useState<Patient[]>(INITIAL_PATIENTS);
  const [residents, setResidents] = useState<Resident[]>(INITIAL_RESIDENTS);
  const [ambientRooms, setAmbientRooms] = useState<AmbientRoom[]>(INITIAL_AMBIENT_ROOMS);
  const [roomSensors, setRoomSensors] = useState<RoomAmbientSensor[]>(INITIAL_ROOM_SENSORS);
  const [homeSeniors, setHomeSeniors] = useState<HomeRPMSenior[]>(INITIAL_HOME_RPM_SENIORS);
  const [deteriorationSignals, setDeteriorationSignals] = useState<EarlyDeteriorationSignal[]>(
    INITIAL_EARLY_DETERIORATION_SIGNALS
  );
  const [selectedPatient, setSelectedPatient] = useState<Patient>(INITIAL_PATIENTS[0]);
  const [selectedResident, setSelectedResident] = useState<Resident>(INITIAL_RESIDENTS[0]);
  const [alerts, setAlerts] = useState<OperationalAlert[]>(INITIAL_ALERTS);
  const [currentDoc, setCurrentDoc] = useState<ClinicalDocument>(SAMPLE_INITIAL_DOC);
  const [isPrintModalOpen, setIsPrintModalOpen] = useState<boolean>(false);
  const [printModalMode, setPrintModalMode] = useState<'clinicalEHR' | 'pocketCard'>('clinicalEHR');
  const [printCenterMode, setPrintCenterMode] = useState<'clinicalEHR' | 'pocketCard'>('clinicalEHR');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [orderToast, setOrderToast] = useState<string | null>(null);
  const [defconStatus, setDefconStatus] = useState<string>('DEFCON-4 (NORMAL)');

  // Multi-User & Clinical Business Access Management State
  const [businesses, setBusinesses] = useState<ClinicalBusiness[]>(() => {
    const saved = localStorage.getItem('medicore_businesses');
    return saved ? JSON.parse(saved) : INITIAL_CLINICAL_BUSINESSES;
  });
  const [users, setUsers] = useState<UserProfile[]>(() => {
    const saved = localStorage.getItem('medicore_users');
    return saved ? JSON.parse(saved) : INITIAL_USERS;
  });
  const [currentBusiness, setCurrentBusiness] = useState<ClinicalBusiness>(() => {
    const saved = localStorage.getItem('medicore_active_business');
    return saved ? JSON.parse(saved) : INITIAL_CLINICAL_BUSINESSES[0];
  });
  const [currentUser, setCurrentUser] = useState<UserProfile>(() => {
    const saved = localStorage.getItem('medicore_active_user');
    return saved ? JSON.parse(saved) : INITIAL_USERS[0];
  });
  const [isAccessModalOpen, setIsAccessModalOpen] = useState<boolean>(false);

  // Sync to local storage
  useEffect(() => {
    localStorage.setItem('medicore_businesses', JSON.stringify(businesses));
    localStorage.setItem('medicore_users', JSON.stringify(users));
    localStorage.setItem('medicore_active_business', JSON.stringify(currentBusiness));
    localStorage.setItem('medicore_active_user', JSON.stringify(currentUser));
  }, [businesses, users, currentBusiness, currentUser]);

  // Family Digest State
  const [familyDigestSubject, setFamilyDigestSubject] = useState<FamilyDigestSubject | null>(null);
  const [isFamilyDigestOpen, setIsFamilyDigestOpen] = useState<boolean>(false);

  // Reference to track last processed IoT cloud radar event ID and IVR keypress acknowledgment
  const lastProcessedEventIdRef = useRef<string | null>(null);
  const lastProcessedAckIsoRef = useRef<string | null>(null);
  const lastProcessedSpatialIsoRef = useRef<string | null>(null);

  // Trigger Hardware mmWave Radar Fall Alert (Supports any room 101, 102, 103, etc.)
  const handleTriggerHardwareFallEvent = (roomNumber: string = '101') => {
    const cleanRoom = String(roomNumber).replace(/^Room\s+/i, '').trim() || '101';
    const matchedRoom = ambientRooms.find((r) => r.roomId === cleanRoom || r.roomNumber.includes(cleanRoom));
    const residentName = matchedRoom?.residentName || (cleanRoom === '102' ? 'Julian Hayes' : cleanRoom === '103' ? 'Robert Chen' : 'Eleanor Vance');
    const residentBg = matchedRoom
      ? `${matchedRoom.age}yo resident (${matchedRoom.primaryDiagnosis})`
      : '84yo female with post-op knee arthroplasty and severe osteoporosis';
    const residentWing = matchedRoom?.wing || 'Wing-A Rehab';

    // Bring the operator to the live room view so the alert is visible immediately
    setCurrentTab('ambientSafety');
    setAmbientRooms((prev) =>
      prev.map((r) =>
        r.roomId === cleanRoom || r.roomNumber.includes(cleanRoom)
          ? {
              ...r,
              posture: 'Fall Alert',
              bedState: 'Out of Bed',
              bedTimeLabel: 'Out of bed: 1 min (Floor Level Confirmed)',
              activeAlert: 'Fall Event',
              alertTimestamp: 'Just now',
              aiSbarNote:
                `SBAR HANDOFF:\nSituation: Physical Aqara FP2 mmWave radar detected STAT Fall Alert in Room ${cleanRoom}. ${residentName} detected at floor level.\nBackground: ${residentBg}.\nAssessment: Sudden loss of balance on ambulation; acute hip/fracture and trauma risk confirmed by 60GHz Doppler telemetry.\nRecommendation: Rapid Response team to bedside immediately; do not move resident prior to cervical/spine stabilization; verify vitals and neuro checks q15m.`
            }
          : r
      )
    );

    const hardwareFallAlert: OperationalAlert = {
      id: `alt-aqara-fall-${Date.now().toString().slice(-4)}`,
      timestamp: 'Just now',
      level: 'CRITICAL',
      title: `EMERGENCY: mmWave Radar Fall Detected (Room ${cleanRoom} - ${residentName})`,
      description: 'Physical Aqara FP2 60GHz radar triggered fall detection via webhook. Rapid Response bedside team activated.',
      department: `${residentWing} / Rapid Response`,
      actionRequired: 'Bedside STAT physical assessment & vitals',
      resolved: false
    };

    setAlerts((prev) => [hardwareFallAlert, ...prev]);
    setOrderToast(`🚨 EMERGENCY: Physical Aqara Radar detected Fall in Room ${cleanRoom} (${residentName})! Rapid Response team logged.`);
    setTimeout(() => setOrderToast(null), 6000);
  };

  // Process incoming multi-sensor hardware events from Cloud Webhook (Aqara FP2, Contact ADL Sensors, Bed Pad, Cellular RPM Vitals)
  const processIncomingHardwareSensorEvent = (event: any, latestDispatch?: any) => {
    const cleanRoom = String(event.roomNumber || '101').replace(/^Room\s+/i, '').trim() || '101';
    const nowTime = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

    if (event.event === 'fall_detected') {
      playFallAlertSound();
      handleTriggerHardwareFallEvent(cleanRoom);
      if (latestDispatch) {
        setOrderToast(`🚨 STAT FALL ALERT: Room ${cleanRoom} (${event.residentName || 'Resident'})! Automated voice call dispatched to ${latestDispatch.recipient}.`);
      }
      return;
    }

    if (event.event === 'bed_exit') {
      setAmbientRooms((prev) =>
        prev.map((r) =>
          r.roomId === cleanRoom || r.roomNumber.includes(cleanRoom)
            ? {
                ...r,
                posture: 'Standing',
                bedState: 'Edge of Bed',
                bedTimeLabel: 'Out of bed: 45 secs (Live Sensor)',
                activeAlert: 'Night Bed Exit',
                alertTimestamp: 'Just now',
                aiSbarNote:
                  `SBAR HANDOFF:\nSituation: Live hardware sensor (${event.deviceId || 'bed-sensor'}) detected night bed-exit in Room ${cleanRoom} (${r.residentName}).\nBackground: ${r.age}yo resident (${r.primaryDiagnosis}).\nAssessment: Unassisted bed boundary exit with elevated fall risk.\nRecommendation: Immediate bedside standby assist to bathroom; verify low-bed mat placement.`
              }
            : r
        )
      );
      setOrderToast(`⚠️ LIVE BED-EXIT SENSOR: Room ${cleanRoom} (${event.residentName || 'Resident'}) boundary exit detected!`);
      setTimeout(() => setOrderToast(null), 6000);
      return;
    }

    if (event.event === 'uti_spike' || event.event === 'adl_event') {
      const sensorType = event.sensorType || (event.event === 'uti_spike' ? 'bathroom' : 'fridge');
      setAmbientRooms((prev) =>
        prev.map((r) => {
          if (r.roomId !== cleanRoom && !r.roomNumber.includes(cleanRoom)) return r;
          const nextVisits = event.bathroomVisitsToday || (sensorType === 'bathroom' ? r.bathroomVisitsToday + 1 : r.bathroomVisitsToday);
          const isUtiSpike = event.event === 'uti_spike' || nextVisits >= 6;
          return {
            ...r,
            posture: isUtiSpike ? 'Walking' : r.posture,
            bathroomVisitsToday: nextVisits,
            utiSpikeAlert: isUtiSpike ? true : r.utiSpikeAlert,
            activeAlert: isUtiSpike ? 'UTI Spike' : r.activeAlert,
            alertTimestamp: isUtiSpike ? 'Just now' : r.alertTimestamp,
            adlActivity: {
              ...r.adlActivity,
              fridgeOpened: sensorType === 'fridge' ? true : r.adlActivity.fridgeOpened,
              fridgeLastOpened: sensorType === 'fridge' ? `Today ${nowTime}` : r.adlActivity.fridgeLastOpened,
              medCabinetOpened: sensorType === 'med_cabinet' ? true : r.adlActivity.medCabinetOpened,
              medCabinetLastOpened: sensorType === 'med_cabinet' ? `Today ${nowTime}` : r.adlActivity.medCabinetLastOpened,
              missedMealRisk: sensorType === 'fridge' ? false : r.adlActivity.missedMealRisk,
              missedMedRisk: sensorType === 'med_cabinet' ? false : r.adlActivity.missedMedRisk
            },
            aiSbarNote: isUtiSpike
              ? `SBAR HANDOFF:\nSituation: Live bathroom occupancy/contact sensor logged ${nextVisits} visits today in Room ${cleanRoom} (${r.residentName}).\nBackground: ${r.age}yo resident (${r.primaryDiagnosis}).\nAssessment: Acute nocturnal/diurnal bathroom spike (>220% over baseline) indicative of early UTI or delirium prodrome.\nRecommendation: Collect STAT clean-catch urinalysis and C&S; monitor oral hydration.`
              : r.aiSbarNote
          };
        })
      );
      setOrderToast(
        event.event === 'uti_spike'
          ? `🔬 LIVE BATHROOM SENSOR: Room ${cleanRoom} UTI frequency spike (${event.bathroomVisitsToday || 8} visits) logged!`
          : `📡 LIVE ADL CONTACT SENSOR: Room ${cleanRoom} ${sensorType.replace('_', ' ')} activity verified at ${nowTime}.`
      );
      setTimeout(() => setOrderToast(null), 6000);
      return;
    }

    if (event.event === 'vitals_reading') {
      setAmbientRooms((prev) =>
        prev.map((r) => {
          if (r.roomId !== cleanRoom && !r.roomNumber.includes(cleanRoom)) return r;
          const nextDelta = event.weightDelta48hLbs !== null && event.weightDelta48hLbs !== undefined
            ? Number(event.weightDelta48hLbs)
            : r.rpmVitals.weightDelta48hLbs;
          return {
            ...r,
            rpmVitals: {
              ...r.rpmVitals,
              cellularBP: event.bp || r.rpmVitals.cellularBP,
              pulseBpm: event.pulseBpm ? Number(event.pulseBpm) : r.rpmVitals.pulseBpm,
              bpTimestamp: `Live ${nowTime}`,
              weightLbs: event.weightLbs ? Number(event.weightLbs) : r.rpmVitals.weightLbs,
              weightDelta48hLbs: nextDelta,
              fluidRetentionAlert: nextDelta >= 3.0
            }
          };
        })
      );
      setOrderToast(`🩺 LIVE CELLULAR RPM VITALS: Room ${cleanRoom} (${event.bp || 'BP/Weight'}) synced at ${nowTime}.`);
      setTimeout(() => setOrderToast(null), 6000);
    }
  };

  // Real-Time IoT Cloud Bridge: Listen for physical Aqara FP2, Contact Sensors, Bed Sensors, Cellular Vitals & Telephony IVR Keypresses
  useEffect(() => {
    let isMounted = true;

    const pollHardwareRadarEvents = async () => {
      try {
        const response = await fetch('https://us-central1-rugged-alcove-388517.cloudfunctions.net/aqaraRadarWebhook?key=mc-radar-sec-7f9a2b4e8c1d3e5f');
        if (!response.ok) return;
        const data = await response.json();

        if (data && isMounted) {
          // 1. Process latest multi-sensor hardware events (fall_detected, bed_exit, uti_spike, adl_event, vitals_reading)
          if (data.latestEvent && data.latestEvent.id) {
            const event = data.latestEvent;
            const eventTime = new Date(event.receivedAt).getTime();
            const isFreshEvent = (Date.now() - eventTime) < 300000; // Received within the last 5 minutes

            if (lastProcessedEventIdRef.current === null) {
              lastProcessedEventIdRef.current = event.id;
              if (isFreshEvent) {
                processIncomingHardwareSensorEvent(event, data.latestDispatch);
              }
            } else if (lastProcessedEventIdRef.current !== event.id) {
              lastProcessedEventIdRef.current = event.id;
              processIncomingHardwareSensorEvent(event, data.latestDispatch);
            }
          }

          // 2. Process telephony IVR keypress acknowledgments (Key 1 = Bedside Dispatched, Key 2 = EMS Escalated)
          if (data.latestAck && data.latestAck.receivedIso) {
            const ack = data.latestAck;
            const ackTime = new Date(ack.receivedIso).getTime();
            const isFreshAck = (Date.now() - ackTime) < 300000;

            if (lastProcessedAckIsoRef.current !== ack.receivedIso && isFreshAck) {
              lastProcessedAckIsoRef.current = ack.receivedIso;

              if (ack.acknowledged) {
                // Keypress 1: Caregiver dispatched, central siren acknowledged!
                setAmbientRooms((prev) =>
                  prev.map((r) =>
                    r.roomId === ack.roomNumber || r.roomNumber.includes(ack.roomNumber)
                      ? {
                          ...r,
                          posture: 'Assisted Bedside Stabilization',
                          bedState: 'Out of Bed (Caregiver Present)',
                          bedTimeLabel: 'Caregiver Dispatched',
                          activeAlert: 'Caregiver Dispatched (Keypress 1)',
                          alertTimestamp: 'Bedside En Route',
                          aiSbarNote:
                            (r.aiSbarNote || '') +
                            `\n\n[TELEPHONY ACKNOWLEDGMENT - KEYPRESS 1]\nCaregiver at ${ack.callerFrom || 'on-duty phone'} acknowledged emergency call via Twilio voice keypress 1.\nAction: Bedside caregiver dispatched immediately. Central trauma siren silenced.`
                        }
                      : r
                  )
                );
                setAlerts((prev) =>
                  prev.map((a) =>
                    a.title.includes(ack.roomNumber)
                      ? { ...a, resolved: true, description: a.description + ' (Caregiver dispatched via phone keypress 1)' }
                      : a
                  )
                );
                setOrderToast(`✅ BEDSIDE NURSE DISPATCHED: Caregiver pressed 1 on phone (${ack.callerFrom || 'mobile'}). Room ${ack.roomNumber} alarm silenced.`);
                setTimeout(() => setOrderToast(null), 8000);
              } else if (ack.escalated) {
                // Keypress 2: Escalated to 911 / EMS!
                setAmbientRooms((prev) =>
                  prev.map((r) =>
                    r.roomId === ack.roomNumber || r.roomNumber.includes(ack.roomNumber)
                      ? {
                          ...r,
                          posture: 'Critical Trauma (Floor Level)',
                          activeAlert: 'STAT 911 EMS Escalated (Keypress 2)',
                          alertTimestamp: 'EMS Dispatched',
                          aiSbarNote:
                            (r.aiSbarNote || '') +
                            `\n\n[CRITICAL TELEPHONY ESCALATION - KEYPRESS 2]\nCaregiver pressed 2 on emergency call.\nAction: Escalated to 911 Emergency Medical Services. Trauma surgeon and paramedic dispatch mobilized.`
                        }
                      : r
                  )
                );
                setOrderToast(`🚨 911 EMS MOBILIZED: Caregiver pressed 2 on emergency call. EMS dispatch notified for Room ${ack.roomNumber}!`);
                setTimeout(() => setOrderToast(null), 8000);
              }
            }
          }

          // 3. Process spatial radar telemetry coordinates (X, Y, Z, zone)
          if (data.latestSpatial && data.latestSpatial.receivedIso) {
            const spatial = data.latestSpatial;
            const spatialTime = new Date(spatial.receivedIso).getTime();
            const isFreshSpatial = (Date.now() - spatialTime) < 120000;

            if (lastProcessedSpatialIsoRef.current === null) {
              lastProcessedSpatialIsoRef.current = spatial.receivedIso;
            } else if (lastProcessedSpatialIsoRef.current !== spatial.receivedIso && isFreshSpatial) {
              lastProcessedSpatialIsoRef.current = spatial.receivedIso;
              if (spatial.isFloorDrop) {
                playFallAlertSound();
                handleTriggerHardwareFallEvent(spatial.roomNumber || '101');
                setOrderToast(`🚨 SPATIAL RADAR DROP DETECTED: Room ${spatial.roomNumber || '101'} (Z=${spatial.coordinates?.z}m in ${spatial.zone})!`);
                setTimeout(() => setOrderToast(null), 7000);
              } else {
                setAmbientRooms((prev) =>
                  prev.map((r) =>
                    r.roomId === (spatial.roomNumber || '101') && r.activeAlert === 'None'
                      ? {
                          ...r,
                          posture: spatial.zone === 'bed_zone' ? 'Bed Resting' : 'Normal Ambulation',
                          bedState: spatial.zone === 'bed_zone' ? 'In Bed' : 'Out of Bed'
                        }
                      : r
                  )
                );
              }
            }
          }
        }
      } catch (err) {
        // Network resilience
      }
    };

    pollHardwareRadarEvents();
    const pollInterval = setInterval(pollHardwareRadarEvents, 2000);

    return () => {
      isMounted = false;
      clearInterval(pollInterval);
    };
  }, []);

  const handleResolveAlert = (id: string) => {
    setAlerts((prev) =>
      prev.map((a) => (a.id === id ? { ...a, resolved: true } : a))
    );
  };

  const handleSimulateAlert = () => {
    // 1. Check user permission
    if (!currentUser.permissions.canTriggerDrills) {
      alert(`Access Restricted: User role "${currentUser.role.replace(/_/g, ' ')}" does not have authorization to trigger clinical drills.`);
      return;
    }

    // 2. Check business license
    if (!currentBusiness.featuresUnlocked.drillSimulations) {
      alert(`License Restriction: ${currentBusiness.name} is operating under a ${currentBusiness.licenseTier.replace(/_/g, ' ')} license. Drill simulations require Commercial Pro or Enterprise Unified.`);
      setIsAccessModalOpen(true);
      return;
    }

    if (currentBusiness.licenseTier === 'ENTERPRISE_UNIFIED') {
      setDefconStatus('DEFCON-2 (SURGE / TRAUMA CODE BLUE)');
      const newAlert: OperationalAlert = {
        id: `alt-${Date.now().toString().slice(-4)}`,
        timestamp: 'Just now',
        level: 'CRITICAL',
        title: 'DEFCON-2 Trauma Surge Activated',
        description: 'Hospital-wide telemetry spike detected. Intensive Care Unit and Trauma Bay 1 on Code Blue alert.',
        department: 'Intensive Care',
        actionRequired: 'Mobilize interventional cardiology & activate trauma surgical team',
        resolved: false
      };
      setAlerts((prev) => [newAlert, ...prev]);
      setPatients((prev) =>
        prev.map((p) =>
          p.id === 'p-101'
            ? {
                ...p,
                status: 'Critical',
                vitals: { ...p.vitals, heartRate: 142, bpSystolic: 80, bpDiastolic: 48, spO2: 89 }
              }
            : p
        )
      );
      setOrderToast('DEFCON-2 Trauma Surge drill initiated! Code Blue dispatch broadcast.');
      setTimeout(() => setOrderToast(null), 4000);
    } else {
      setDefconStatus('DEFCON-3 (RAPID RESPONSE / FALL CODE)');
      const newAlert: OperationalAlert = {
        id: `alt-${Date.now().toString().slice(-4)}`,
        timestamp: 'Just now',
        level: 'WARNING',
        title: 'DEFCON-3 Senior Living Care Drill',
        description: 'Simulated fall warning in Room 102. Rapid response team mobilized for bed transfer assessment.',
        department: 'Senior Living',
        actionRequired: 'Bedside nurse evaluation & physical assist',
        resolved: false
      };
      setAlerts((prev) => [newAlert, ...prev]);
      setOrderToast('DEFCON-3 Senior Living Rapid Response drill initiated!');
      setTimeout(() => setOrderToast(null), 4000);
    }
  };

  const handleResetDrill = () => {
    setDefconStatus('DEFCON-4 (NORMAL)');
    setOrderToast('DEFCON status normalized to DEFCON-4 (NORMAL).');
    setTimeout(() => setOrderToast(null), 3000);
  };

  // License Upgrade handler
  const handleUpgradeLicense = (bizId: string, newKey: string, tier: LicenseTier) => {
    const updated = businesses.map((b) => {
      if (b.id !== bizId) return b;
      return {
        ...b,
        licenseKey: newKey,
        licenseTier: tier,
        trialDaysRemaining: tier === 'BETA_TRIAL' ? 30 : undefined,
        authorizedMaxBeds: tier === 'ENTERPRISE_UNIFIED' ? 250 : tier === 'COMMERCIAL_PRO' ? 60 : 25,
        featuresUnlocked: {
          cptBillingLedger: tier !== 'BETA_TRIAL',
          digitalShaSignature: tier === 'ENTERPRISE_UNIFIED',
          voiceBiomarkersDSP: true,
          ambientRadarEmergency: true,
          drillSimulations: tier !== 'BETA_TRIAL'
        }
      };
    });

    setBusinesses(updated);
    const updatedCurrent = updated.find((b) => b.id === currentBusiness.id);
    if (updatedCurrent) setCurrentBusiness(updatedCurrent);

    setOrderToast(`Business license updated to ${tier.replace(/_/g, ' ')}!`);
    setTimeout(() => setOrderToast(null), 4000);
  };

  // Simulation 1: Fall Event (Room 101 - Eleanor Vance)
  const handleSimulateFallEvent = () => {
    playFallAlertSound();
    setAmbientRooms((prev) =>
      prev.map((r) =>
        r.roomId === '101'
          ? {
              ...r,
              posture: 'Fall Alert',
              bedState: 'Out of Bed',
              bedTimeLabel: 'Out of bed: 1 min',
              activeAlert: 'Fall Event',
              alertTimestamp: 'Just now',
              aiSbarNote:
                'SBAR HANDOFF:\nSituation: mmWave radar triggered STAT Fall Alert in Room 101. Eleanor Vance detected at floor level.\nBackground: 84yo female with post-op knee arthroplasty and severe osteoporosis.\nAssessment: Sudden loss of balance on ambulation; acute hip/fracture and trauma risk.\nRecommendation: Rapid Response team to bedside immediately; do not move resident prior to cervical/spine stabilization; verify vitals and neuro checks q15m.'
            }
          : r
      )
    );

    const fallAlert: OperationalAlert = {
      id: `alt-fall-101-${Date.now().toString().slice(-4)}`,
      timestamp: 'Just now',
      level: 'CRITICAL',
      title: 'EMERGENCY: mmWave Fall Detected (Room 101 - Eleanor Vance)',
      description: 'Floor level posture confirmed by sub-mm Doppler radar. Rapid Response bedside team activated.',
      department: 'Wing-A Rehab / Rapid Response',
      actionRequired: 'Bedside STAT physical assessment & vitals',
      resolved: false
    };

    setAlerts((prev) => [fallAlert, ...prev]);
    setOrderToast('EMERGENCY: Room 101 Fall Alert triggered! Rapid Response logged.');
    setTimeout(() => setOrderToast(null), 5000);
  };

  // Simulation 2: Night Bed Exit (Room 102 - Julian Hayes)
  const handleSimulateBedExit = () => {
    setAmbientRooms((prev) =>
      prev.map((r) =>
        r.roomId === '102'
          ? {
              ...r,
              posture: 'Standing',
              bedState: 'Edge of Bed',
              bedTimeLabel: 'Out of bed: 45 secs',
              activeAlert: 'Night Bed Exit',
              alertTimestamp: 'Just now',
              aiSbarNote:
                'SBAR HANDOFF:\nSituation: Micro-pressure sensor detected 45-second night bed exit for Julian Hayes (Room 102).\nBackground: Parkinsonian freezing of gait and nocturnal wandering risk.\nAssessment: Imminent unassisted ambulation with 0.72 m/s gait speed places resident at highest fall risk.\nRecommendation: Night nurse audio chime intervention; prompt bedside 1-person standby assist to bathroom; activate low-bed mat.'
            }
          : r
      )
    );

    const bedExitAlert: OperationalAlert = {
      id: `alt-bed-102-${Date.now().toString().slice(-4)}`,
      timestamp: 'Just now',
      level: 'WARNING',
      title: 'Night Bed-Exit Prevention Alert (Room 102 - Julian Hayes)',
      description: '45-second boundary threshold reached. Night nurse prompt dispatched to intercept fall.',
      department: 'Wing-B Memory Care',
      actionRequired: 'Provide 1-person standby assist',
      resolved: false
    };

    setAlerts((prev) => [bedExitAlert, ...prev]);
    setOrderToast('Room 102 Night Bed-Exit alert active (45 seconds out of bed)');
    setTimeout(() => setOrderToast(null), 4500);
  };

  // Simulation 3: UTI Spike (Room 103 - Robert Chen)
  const handleSimulateUTISpike = () => {
    setAmbientRooms((prev) =>
      prev.map((r) =>
        r.roomId === '103'
          ? {
              ...r,
              bathroomVisitsToday: 8,
              lastBathroomTime: 'Just now',
              activeAlert: 'UTI Spike',
              alertTimestamp: 'Just now',
              aiSbarNote:
                'SBAR HANDOFF:\nSituation: Bathroom tracker in Room 103 recorded 8 voiding visits today (>6 trigger threshold).\nBackground: Robert Chen has Stage 3 CKD and history of occult bacterial cystitis.\nAssessment: Classic prodromal UTI presentation with autonomic instability and gait slowing (0.68 m/s).\nRecommendation: Order STAT dipstick urinalysis, enforce 8 oz oral hydration protocol every 2 hours, and notify attending physician.'
            }
          : r
      )
    );

    const utiAlert: OperationalAlert = {
      id: `alt-uti-103-${Date.now().toString().slice(-4)}`,
      timestamp: 'Just now',
      level: 'WARNING',
      title: 'Suspected UTI Spike (Room 103 - Robert Chen)',
      description: '8 bathroom visits today with slowing gait. AI Proactive Interventions active.',
      department: 'Wing-C Assisted Living',
      actionRequired: 'Order Urinalysis & Hydration Check',
      resolved: false
    };

    setAlerts((prev) => [utiAlert, ...prev]);
    setOrderToast('Room 103 Bathroom Count spiked to 8! Suspected UTI Risk flagged.');
    setTimeout(() => setOrderToast(null), 4500);
  };

  const handleResetSimulation = () => {
    setAmbientRooms(INITIAL_AMBIENT_ROOMS);
    setAlerts(INITIAL_ALERTS);
    fetch('https://us-central1-rugged-alcove-388517.cloudfunctions.net/aqaraRadarWebhook?action=reset').catch(() => {});
    setOrderToast('All ambient room sensors & operational alerts reset to normal baseline.');
    setTimeout(() => setOrderToast(null), 3000);
  };

  const handleUpdateRoomSbar = (roomId: string, note: string) => {
    setAmbientRooms((prev) =>
      prev.map((r) => (r.roomId === roomId ? { ...r, aiSbarNote: note } : r))
    );
  };

  const handleResetResidentTurn = (residentId: string) => {
    const now = new Date();
    const timeStr = now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

    setResidents((prev) =>
      prev.map((r) => {
        if (r.id !== residentId) return r;
        const nextSide: Resident['turnSide'] =
          r.turnSide === 'Left Lateral'
            ? 'Supine'
            : r.turnSide === 'Supine'
            ? 'Right Lateral'
            : 'Left Lateral';

        return {
          ...r,
          turnMinutesRemaining: 120,
          lastTurnTimestamp: timeStr,
          turnSide: nextSide
        };
      })
    );
  };

  const handleToggleFallWarning = (roomId: string) => {
    setRoomSensors((prev) =>
      prev.map((room) => {
        if (room.roomId !== roomId) return room;
        const isCurrentlyWarning = room.mmWaveRadar.status === 'Fall Warning';
        return {
          ...room,
          mmWaveRadar: {
            ...room.mmWaveRadar,
            status: isCurrentlyWarning ? 'Normal' : 'Fall Warning',
            posture: isCurrentlyWarning ? 'Standing' : 'Floor Level (Alert)',
            lastDetected: 'Just now'
          }
        };
      })
    );
  };

  const handleClearUTIAlert = (roomId: string) => {
    setRoomSensors((prev) =>
      prev.map((room) =>
        room.roomId === roomId
          ? {
              ...room,
              bathroomTracker: {
                ...room.bathroomTracker,
                utiRiskAlert: false,
                visitsToday: 4
              }
            }
          : room
      )
    );
  };

  const handlePlaceSentryOrder = (signalId: string) => {
    // Check permission
    if (!currentUser.permissions.canPlaceLabOrders) {
      alert(`Access Restricted: Role "${currentUser.role}" does not have prescriptive lab requisition privileges.`);
      return;
    }

    const now = new Date();
    const timeStr = now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const reqId = `STAT-UA-${Math.floor(1000 + Math.random() * 9000)}`;

    setDeteriorationSignals((prev) =>
      prev.map((sig) => {
        if (sig.id !== signalId) return sig;
        return {
          ...sig,
          orderPlaced: true,
          orderTimestamp: `Today at ${timeStr} EST`,
          orderRequisitionId: reqId
        };
      })
    );

    const targetSignal = deteriorationSignals.find((s) => s.id === signalId);
    const targetName = targetSignal ? targetSignal.seniorName : 'Patient';

    const orderAlert: OperationalAlert = {
      id: `alt-ord-${Date.now().toString().slice(-4)}`,
      timestamp: 'Just now',
      level: 'INFO',
      title: `STAT Urinalysis & Hydration Dispatched: ${targetName}`,
      description: `Requisition ${reqId} generated by ${currentUser.fullName}. Floor nurse instructed to collect specimen and verify fluid intake.`,
      department: 'Senior Living / RPM',
      actionRequired: 'Nursing collect specimen & verify fluid intake',
      resolved: false
    };

    setAlerts((prev) => [orderAlert, ...prev]);

    setOrderToast(
      `STAT Urinalysis & Hydration Check ordered for ${targetName} (Requisition #${reqId})`
    );
    setTimeout(() => {
      setOrderToast(null);
    }, 4500);
  };

  const handleOpenPocketCard = (resident: Resident) => {
    setSelectedResident(resident);
    setPrintModalMode('pocketCard');
    setIsPrintModalOpen(true);
  };

  const handleOpenFamilyDigest = (subject: FamilyDigestSubject) => {
    setFamilyDigestSubject(subject);
    setIsFamilyDigestOpen(true);
  };

  const handleSendFamilySMS = (recipientName: string, phone: string, message: string) => {
    if (!currentUser.permissions.canSendFamilySMS) {
      alert(`Access Restricted: User "${currentUser.fullName}" is not authorized for direct telecommunications dispatch.`);
      return;
    }

    const smsAlert: OperationalAlert = {
      id: `sms-${Date.now().toString().slice(-4)}`,
      timestamp: 'Just now',
      level: 'INFO',
      title: `Family Digest SMS Dispatched: ${recipientName}`,
      description: `Encrypted cellular SMS transmitted to ${phone} by ${currentUser.fullName}: "${message.slice(0, 80)}..."`,
      department: 'Family Communication Hub',
      actionRequired: 'Carrier transmission verified',
      resolved: true
    };

    setAlerts((prev) => [smsAlert, ...prev]);

    setOrderToast(`Cellular SMS successfully sent to ${recipientName} (${phone})`);
    setTimeout(() => {
      setOrderToast(null);
    }, 4500);
  };

  const turnsDueSoonCount = residents.filter((r) => r.turnMinutesRemaining <= 30).length;
  const activeSensorAlertCount =
    roomSensors.filter((s) => s.mmWaveRadar.status === 'Fall Warning').length +
    roomSensors.filter((s) => s.bathroomTracker.utiRiskAlert).length +
    deteriorationSignals.filter((s) => !s.orderPlaced).length;

  const activeAmbientAlertCount = ambientRooms.filter(
    (r) => r.activeAlert !== 'None' && !r.activeAlert.startsWith('Caregiver Dispatched')
  ).length;

  const filteredPatients = patients.filter(
    (p) =>
      p.fullName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.mrn.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.department.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col font-sans">
      {/* Top OS Navigation Header with Multi-Business Entity & Access Management */}
      <Navbar
        systemStatus={defconStatus}
        onQuickSimulateAlert={handleSimulateAlert}
        unresolvedAlertCount={alerts.filter((a) => !a.resolved).length}
        currentBusiness={currentBusiness}
        currentUser={currentUser}
        onOpenAccessModal={() => setIsAccessModalOpen(true)}
        onResetDrill={handleResetDrill}
        onNavigateToAmbient={() => setCurrentTab('ambientSafety')}
      />

      {/* Beta Trial Banner if current clinical entity is on trial */}
      {currentBusiness.licenseTier === 'BETA_TRIAL' && (
        <div className="bg-amber-950/60 border-b border-amber-500/40 px-6 py-2 flex items-center justify-between text-xs text-amber-200">
          <div className="flex items-center gap-2">
            <KeyRound className="h-4 w-4 text-amber-400 shrink-0" />
            <span>
              <strong>BETA TRIAL EVALUATION MODE:</strong> {currentBusiness.name} has {currentBusiness.trialDaysRemaining || 14} days remaining. Data is stored on this device.
            </span>
          </div>
          <button
            onClick={() => setIsAccessModalOpen(true)}
            className="px-2.5 py-1 rounded bg-amber-400 hover:bg-amber-300 text-slate-950 font-bold text-[11px] transition"
          >
            Upgrade to Paid License
          </button>
        </div>
      )}

      {/* Floating Action Toast for STAT Orders & SMS Sent */}
      {orderToast && (
        <div className="fixed top-20 right-6 z-50 bg-emerald-950 border-2 border-emerald-500 text-emerald-200 px-4 py-3 rounded-2xl shadow-2xl flex items-center gap-3 animate-slide-in">
          <div className="h-8 w-8 rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-400 font-bold">
            ✓
          </div>
          <div>
            <p className="font-bold text-xs text-white">Clinical Operation Notice</p>
            <p className="text-[11px] text-emerald-300 font-mono">{orderToast}</p>
          </div>
        </div>
      )}

      {/* Main Framework Layout with Sidebar and Active Stage */}
      <div className="flex-1 flex overflow-hidden">
        <Sidebar
          currentTab={currentTab}
          onSelectTab={setCurrentTab}
          unresolvedAlertCount={alerts.filter((a) => !a.resolved).length}
          activeDocCount={1}
          turnsDueSoonCount={turnsDueSoonCount}
          activeSensorAlertCount={activeSensorAlertCount}
          activeAmbientAlertCount={activeAmbientAlertCount}
        />

        <main className="flex-1 overflow-y-auto p-6 bg-slate-950/90">
          {/* Global Search Bar */}
          <div className="mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-xl font-extrabold text-white tracking-tight flex items-center gap-2">
                <span>
                  {currentTab === 'command' && 'Executive Command & Intelligence Center'}
                  {currentTab === 'ambientSafety' && 'Ambient RPM & Senior Safety Hub'}
                  {currentTab === 'dashboard' && 'Clinical Telemetry & Department Flow'}
                  {currentTab === 'remoteRPM' && 'Remote Care & Ambient RPM Intelligence Hub'}
                  {currentTab === 'seniorLiving' && 'Senior Living & Caregiver Operations Suite'}
                  {currentTab === 'wellness' && 'Preventive Longevity & Wellness Coaching Hub'}
                  {currentTab === 'clinicalDocs' && 'Intelligent Clinical Documentation (EHR)'}
                  {currentTab === 'patients' && 'Active Hospital Census & Patient Registry'}
                  {currentTab === 'printCenter' && 'Official EHR Print & Audit Verification'}
                </span>
              </h1>
              <p className="text-xs text-slate-400 mt-0.5">
                {currentBusiness.name} • Workspace Access: {currentUser.role.replace(/_/g, ' ')} ({currentBusiness.licenseTier.replace(/_/g, ' ')})
              </p>
            </div>

            <div className="relative w-full sm:w-72">
              <Search className="h-4 w-4 absolute left-3 top-2.5 text-slate-500" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search patient, MRN, room or wing..."
                className="w-full bg-slate-900 border border-slate-800 rounded-xl pl-9 pr-3 py-1.5 text-xs text-slate-200 placeholder-slate-500 focus:outline-none focus:border-teal-500"
              />
            </div>
          </div>

          {/* Tab Views */}
          {currentTab === 'command' && (
            <CommandCenter
              departmentMetrics={INITIAL_DEPARTMENT_METRICS}
              alerts={alerts}
              patients={filteredPatients}
              deteriorationSignals={deteriorationSignals}
              onSelectPatient={(pt) => {
                setSelectedPatient(pt);
                setCurrentTab('clinicalDocs');
              }}
              onResolveAlert={handleResolveAlert}
              onNavigateToDocs={() => setCurrentTab('clinicalDocs')}
              onPlaceSentryOrder={handlePlaceSentryOrder}
              currentBusiness={currentBusiness}
              onOpenAccessModal={() => setIsAccessModalOpen(true)}
              onNavigateToAmbient={() => setCurrentTab('ambientSafety')}
            />
          )}

          {currentTab === 'ambientSafety' && (
            <AmbientSafetyDashboard
              rooms={ambientRooms}
              onSimulateFall={handleSimulateFallEvent}
              onTriggerHardwareFall={handleTriggerHardwareFallEvent}
              onSimulateBedExit={handleSimulateBedExit}
              onSimulateUTISpike={handleSimulateUTISpike}
              onResetSimulation={handleResetSimulation}
              onOpenFamilyDigest={handleOpenFamilyDigest}
              onUpdateRoomSbar={handleUpdateRoomSbar}
            />
          )}

          {currentTab === 'dashboard' && (
            <Dashboard
              patients={filteredPatients}
              onSelectPatient={(pt) => {
                setSelectedPatient(pt);
                setCurrentTab('clinicalDocs');
              }}
              onOpenDocForPatient={(pt) => {
                setSelectedPatient(pt);
                setCurrentTab('clinicalDocs');
              }}
              onOpenFamilyDigest={handleOpenFamilyDigest}
            />
          )}

          {currentTab === 'remoteRPM' && (
            <RemoteCareRPM
              roomSensors={roomSensors}
              homeSeniors={homeSeniors}
              deteriorationSignals={deteriorationSignals}
              onToggleFallWarning={handleToggleFallWarning}
              onClearUTIAlert={handleClearUTIAlert}
              onPlaceSentryOrder={handlePlaceSentryOrder}
              onOpenFamilyDigest={handleOpenFamilyDigest}
              currentBusiness={currentBusiness}
              onOpenAccessModal={() => setIsAccessModalOpen(true)}
            />
          )}

          {currentTab === 'seniorLiving' && (
            <SeniorLivingSuite
              residents={residents}
              onResetTurn={handleResetResidentTurn}
              onOpenPocketCard={handleOpenPocketCard}
              onOpenFamilyDigest={handleOpenFamilyDigest}
            />
          )}

          {currentTab === 'wellness' && (
            <PreventiveWellnessSuite
              patient={selectedPatient}
              patients={patients}
              onSelectPatient={setSelectedPatient}
              ambientRooms={ambientRooms}
              onOpenFamilyDigest={handleOpenFamilyDigest}
              onSyncConsumerAdl={(roomId, type) => {
                const nowTime = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                setAmbientRooms((prev) =>
                  prev.map((r) =>
                    r.roomId === roomId
                      ? {
                          ...r,
                          adlActivity: {
                            ...r.adlActivity,
                            fridgeOpened: type === 'nutrition' ? true : r.adlActivity.fridgeOpened,
                            fridgeLastOpened: type === 'nutrition' ? `Consumer App ${nowTime}` : r.adlActivity.fridgeLastOpened,
                            missedMealRisk: type === 'nutrition' ? false : r.adlActivity.missedMealRisk,
                            medCabinetOpened: type === 'medication' ? true : r.adlActivity.medCabinetOpened,
                            medCabinetLastOpened: type === 'medication' ? `Consumer App ${nowTime}` : r.adlActivity.medCabinetLastOpened,
                            missedMedRisk: type === 'medication' ? false : r.adlActivity.missedMedRisk
                          }
                        }
                      : r
                  )
                );
                setOrderToast(
                  type === 'medication'
                    ? `📱 CONSUMER APP -> CLINICAL HUB SYNC: Room ${roomId} medication adherence verified at ${nowTime}!`
                    : `📱 CONSUMER APP -> CLINICAL HUB SYNC: Room ${roomId} hydration/meal logged & synced at ${nowTime}!`
                );
                setTimeout(() => setOrderToast(null), 5000);
              }}
            />
          )}

          {currentTab === 'clinicalDocs' && (
            <ClinicalDocs
              patients={patients}
              selectedPatient={selectedPatient}
              onSelectPatient={setSelectedPatient}
              activeDoc={currentDoc}
              onUpdateDoc={setCurrentDoc}
              onOpenPrintPreview={() => {
                setPrintModalMode('clinicalEHR');
                setIsPrintModalOpen(true);
              }}
            />
          )}

          {currentTab === 'patients' && (
            <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-5 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-bold text-white">Active Inpatient Census</h3>
                  <p className="text-xs text-slate-400">Search and launch clinical document sessions</p>
                </div>
                <span className="text-xs font-mono px-2 py-0.5 rounded bg-slate-800 text-teal-300">
                  {filteredPatients.length} Patients
                </span>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead className="bg-slate-950/70 border-b border-slate-800 font-mono text-[11px] text-slate-400">
                    <tr>
                      <th className="p-3">Patient & MRN</th>
                      <th className="p-3">Department</th>
                      <th className="p-3">Status</th>
                      <th className="p-3">Heart Rate</th>
                      <th className="p-3">SpO2</th>
                      <th className="p-3">Family Contact</th>
                      <th className="p-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800/60">
                    {filteredPatients.map((p) => (
                      <tr key={p.id} className="hover:bg-slate-800/30 transition">
                        <td className="p-3">
                          <div className="font-semibold text-slate-200">{p.fullName}</div>
                          <div className="text-[10px] text-teal-400 font-mono">{p.mrn}</div>
                        </td>
                        <td className="p-3">
                          <div>{p.department}</div>
                          <div className="text-[10px] text-slate-500 font-mono">{p.bedNumber}</div>
                        </td>
                        <td className="p-3">
                          <span
                            className={`text-[10px] font-mono px-2 py-0.5 rounded font-bold ${
                              p.status === 'Critical'
                                ? 'bg-rose-500 text-slate-950'
                                : p.status === 'Guarded'
                                ? 'bg-amber-400/20 text-amber-300'
                                : 'bg-emerald-500/20 text-emerald-300'
                            }`}
                          >
                            {p.status}
                          </span>
                        </td>
                        <td className="p-3 font-mono">{p.vitals.heartRate} bpm</td>
                        <td className="p-3 font-mono">{p.vitals.spO2}%</td>
                        <td className="p-3 text-slate-300">
                          <div>{p.familyContact.name}</div>
                          <div className="text-[10px] text-slate-500 font-mono">{p.familyContact.phone}</div>
                        </td>
                        <td className="p-3 text-right space-x-2">
                          <button
                            onClick={() => {
                              handleOpenFamilyDigest({
                                id: p.id,
                                name: p.fullName,
                                recipientName: p.familyContact.name,
                                recipientRelation: p.familyContact.relation,
                                recipientPhone: p.familyContact.phone,
                                sleepScoreOrState: 'Resting comfortably on telemetry monitoring',
                                vitalsSummary: `BP ${p.vitals.bpSystolic}/${p.vitals.bpDiastolic} mmHg, HR ${p.vitals.heartRate} bpm, SpO2 ${p.vitals.spO2}%`,
                                nutritionActivitySummary: `Bedside meal intake completed on schedule, attending physician: ${p.attendingPhysician}`,
                                locationOrRoom: `${p.department} (${p.bedNumber})`
                              });
                            }}
                            className="bg-pink-500/10 hover:bg-pink-500/20 text-pink-300 border border-pink-500/30 px-2.5 py-1 rounded text-xs transition"
                          >
                            Family Digest
                          </button>
                          <button
                            onClick={() => {
                              setSelectedPatient(p);
                              setCurrentTab('clinicalDocs');
                            }}
                            className="bg-teal-500/10 hover:bg-teal-500/20 text-teal-300 border border-teal-500/30 px-2.5 py-1 rounded text-xs transition"
                          >
                            Clinical Docs
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {currentTab === 'printCenter' && (
            <div className="space-y-6">
              {/* Header Controls Bar */}
              <div className="bg-slate-900 border border-slate-800 rounded-2xl p-4 flex flex-col md:flex-row items-center justify-between gap-4 shadow-xl">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 rounded-xl bg-teal-500/20 border border-teal-500/40 text-teal-300">
                    <Printer className="h-6 w-6" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="text-base font-bold text-white">
                        Official EHR Print &amp; Certified Legal Verification Center
                      </h2>
                      <span className="bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 text-[10px] font-mono px-2 py-0.5 rounded font-bold uppercase">
                        ★ SHA-256 Validated
                      </span>
                    </div>
                    <p className="text-xs text-slate-400">
                      Official tamper-evident inpatient medical records signed with MediCore AI Unified Cryptographic Ledger.
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2.5">
                  <div className="flex items-center bg-slate-950 p-1 rounded-xl border border-slate-800 text-xs">
                    <button
                      onClick={() => setPrintCenterMode('clinicalEHR')}
                      className={`px-3.5 py-1.5 rounded-lg font-bold transition flex items-center gap-1.5 ${
                        printCenterMode === 'clinicalEHR'
                          ? 'bg-teal-500 text-slate-950 shadow-md'
                          : 'text-slate-400 hover:text-white'
                      }`}
                    >
                      <FileText className="h-3.5 w-3.5" />
                      <span>Certified Clinical EHR Note</span>
                    </button>
                    <button
                      onClick={() => setPrintCenterMode('pocketCard')}
                      className={`px-3.5 py-1.5 rounded-lg font-bold transition flex items-center gap-1.5 ${
                        printCenterMode === 'pocketCard'
                          ? 'bg-indigo-600 text-white shadow-md'
                          : 'text-slate-400 hover:text-white'
                      }`}
                    >
                      <ShieldCheck className="h-3.5 w-3.5" />
                      <span>Caregiver Pocket Card ({selectedResident.preferredName || selectedResident.fullName.split(' ')[0]})</span>
                    </button>
                  </div>

                  <button
                    onClick={() => window.print()}
                    className="flex items-center gap-2 bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-400 hover:to-emerald-400 text-slate-950 font-bold px-4 py-2 rounded-xl transition text-xs shadow-lg shadow-teal-500/25 active:scale-95 shrink-0"
                    title="Print certified document or pocket card"
                  >
                    <Printer className="h-4 w-4" />
                    <span>Print Official Document</span>
                  </button>
                </div>
              </div>

              {/* Resident Selector when in Pocket Card Mode */}
              {printCenterMode === 'pocketCard' && (
                <div className="bg-slate-900 border border-slate-800 rounded-2xl p-3 flex flex-wrap items-center justify-between gap-3 shadow-md no-print">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-slate-300">Select Resident for Pocket Card:</span>
                    <span className="text-xs text-indigo-400 font-mono font-bold bg-indigo-950/60 px-2 py-0.5 rounded border border-indigo-500/40">
                      {selectedResident.fullName} ({selectedResident.roomNumber})
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5">
                    {residents.map((r) => (
                      <button
                        key={r.id}
                        onClick={() => setSelectedResident(r)}
                        className={`px-3 py-1 rounded-lg text-xs font-semibold transition ${
                          selectedResident.id === r.id
                            ? 'bg-indigo-600 text-white shadow-md border border-indigo-400'
                            : 'bg-slate-800 text-slate-300 hover:text-white hover:bg-slate-700 border border-slate-700'
                        }`}
                      >
                        {r.fullName}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Direct Inline Render of Document */}
              <div className="flex justify-center p-2">
                {printCenterMode === 'clinicalEHR' ? (
                  /* Standard Hospital Letterhead Document with Legal SHA-256 Seal */
                  <div
                    id="printable-document"
                    className="w-full max-w-[820px] bg-white text-slate-900 p-8 sm:p-12 rounded-xl shadow-2xl font-sans text-xs border border-slate-300"
                  >
                    {/* Letterhead */}
                    <div className="border-b-2 border-slate-900 pb-4 flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="h-4 w-4 rounded-full bg-teal-700"></span>
                          <h1 className="text-xl font-black text-slate-900 tracking-tight">
                            {currentBusiness.name.toUpperCase()}
                          </h1>
                        </div>
                        <p className="text-[11px] text-slate-600 font-medium">
                          {currentBusiness.facilityType} • Division of Trauma, Telemetry &amp; Senior Care
                        </p>
                        <p className="text-[10px] text-slate-500">
                          1400 Medical Center Parkway • NPI: {currentBusiness.activeNpi} • CLIA: 99D2049182
                        </p>
                      </div>
                      <div className="text-right">
                        <span className="font-mono text-[10px] font-bold bg-slate-100 border border-slate-300 px-2 py-1 rounded block">
                          CONFIDENTIAL MEDICAL RECORD
                        </span>
                        <p className="text-[10px] text-slate-500 mt-1">DOC ID: {currentDoc.id}</p>
                      </div>
                    </div>

                    {/* Patient Demographic Banner */}
                    <div className="bg-slate-100 p-3 rounded-lg border border-slate-200 mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px]">
                      <div>
                        <span className="text-slate-500 block text-[9px] uppercase font-bold">Patient Name</span>
                        <strong className="text-slate-900">{currentDoc.patientName || selectedPatient.fullName}</strong>
                      </div>
                      <div>
                        <span className="text-slate-500 block text-[9px] uppercase font-bold">MRN / Record #</span>
                        <span className="font-mono text-slate-800">{currentDoc.mrn || selectedPatient.mrn}</span>
                      </div>
                      <div>
                        <span className="text-slate-500 block text-[9px] uppercase font-bold">Age / Gender / Blood</span>
                        <span className="text-slate-800">{selectedPatient.age}y • {selectedPatient.gender} ({selectedPatient.bloodType})</span>
                      </div>
                      <div>
                        <span className="text-slate-500 block text-[9px] uppercase font-bold">Attending Physician</span>
                        <span className="text-slate-800">{selectedPatient.attendingPhysician || 'Dr. Marcus Sterling, MD'}</span>
                      </div>
                    </div>

                    {/* Clinical Note Content */}
                    <div className="mt-6 space-y-4">
                      <div className="border-b border-slate-200 pb-2">
                        <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wide">
                          {currentDoc.docType} — Official Inpatient Clinical Record
                        </h2>
                        <span className="text-[10px] font-mono text-slate-500">
                          Encounter Date: {currentDoc.date} • Unit: {selectedPatient.department} ({selectedPatient.bedNumber})
                        </span>
                      </div>

                      <div className="space-y-3 text-slate-800 leading-relaxed text-xs">
                        <div>
                          <strong className="text-slate-900 uppercase text-[10px] tracking-wider block">Chief Complaint:</strong>
                          <p className="mt-0.5 pl-2 border-l-2 border-slate-400 font-medium">{currentDoc.chiefComplaint}</p>
                        </div>
                        <div>
                          <strong className="text-slate-900 uppercase text-[10px] tracking-wider block">1. Subjective:</strong>
                          <p className="mt-0.5 pl-2 border-l border-slate-300 whitespace-pre-line">{currentDoc.subjective}</p>
                        </div>
                        <div>
                          <strong className="text-slate-900 uppercase text-[10px] tracking-wider block">2. Objective:</strong>
                          <p className="mt-0.5 pl-2 border-l border-slate-300 whitespace-pre-line">{currentDoc.objective}</p>
                        </div>
                        <div>
                          <strong className="text-slate-900 uppercase text-[10px] tracking-wider block">3. Assessment:</strong>
                          <p className="mt-0.5 pl-2 border-l border-slate-300 whitespace-pre-line">{currentDoc.assessment}</p>
                        </div>
                        <div>
                          <strong className="text-slate-900 uppercase text-[10px] tracking-wider block">4. Plan:</strong>
                          <p className="mt-0.5 pl-2 border-l border-slate-300 whitespace-pre-line">{currentDoc.plan}</p>
                        </div>
                      </div>

                      {/* Prescriptions Table */}
                      {currentDoc.prescriptions && currentDoc.prescriptions.length > 0 && (
                        <div className="mt-4 pt-2">
                          <h3 className="font-bold text-slate-800 text-[10px] uppercase tracking-wider mb-1">
                            ACTIVE INPATIENT &amp; DISCHARGE MEDICATIONS:
                          </h3>
                          <table className="w-full text-left text-[10px] border border-slate-300">
                            <thead className="bg-slate-100 border-b border-slate-300 font-bold text-slate-700">
                              <tr>
                                <th className="p-1.5">Medication</th>
                                <th className="p-1.5">Dosage</th>
                                <th className="p-1.5">Frequency</th>
                                <th className="p-1.5">Route</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-200">
                              {currentDoc.prescriptions.map((rx, idx) => (
                                <tr key={idx}>
                                  <td className="p-1.5 font-bold text-slate-900">{rx.drug}</td>
                                  <td className="p-1.5 font-mono">{rx.dosage}</td>
                                  <td className="p-1.5">{rx.frequency}</td>
                                  <td className="p-1.5">{rx.route}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {/* ICD-10 Coding */}
                      {currentDoc.icd10Codes && currentDoc.icd10Codes.length > 0 && (
                        <div className="mt-4 pt-2">
                          <h3 className="font-bold text-slate-800 text-[10px] uppercase tracking-wider mb-1">
                            RECORDED ICD-10 DIAGNOSES:
                          </h3>
                          <div className="flex flex-wrap gap-2">
                            {currentDoc.icd10Codes.map((code, idx) => (
                              <span key={idx} className="bg-slate-100 border border-slate-300 px-2 py-0.5 rounded text-[10px] text-slate-800">
                                <strong>{code.code}</strong> — {code.description}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Official Signature and Legal SHA-256 Seal */}
                      <div className="mt-10 pt-6 border-t-2 border-slate-900 flex flex-col sm:flex-row items-start sm:items-end justify-between gap-4">
                        <div>
                          <p className="font-bold text-slate-900 text-xs">{currentDoc.author || 'Dr. Marcus Sterling, MD'}</p>
                          <p className="text-[10px] text-slate-600">Chief Medical Officer • License #MD-884920</p>
                          <div className="mt-2 flex items-center gap-2 text-emerald-800 font-mono text-[9px]">
                            <ShieldCheck className="h-3.5 w-3.5 text-emerald-700" />
                            <span>Digitally Validated with MediCore AI Unified Ledger</span>
                          </div>
                        </div>

                        {/* PROMINENT LEGAL SHA-256 DIGITAL VERIFICATION SEAL */}
                        <div className="border-2 border-emerald-700 bg-emerald-50 px-4 py-2.5 rounded-xl shadow-md text-right max-w-sm">
                          <span className="text-[10px] font-mono font-black text-emerald-800 uppercase block tracking-wider flex items-center justify-end gap-1">
                            <span>★ OFFICIAL HOSPITAL SEAL</span>
                          </span>
                          <span className="text-[9px] font-mono text-emerald-700 block font-bold mt-0.5">
                            CERTIFIED LEGAL EHR RECORD
                          </span>
                          <p className="font-mono text-[8px] text-emerald-950 font-bold mt-1 break-all bg-emerald-100/70 p-1 rounded border border-emerald-300">
                            {currentDoc.verificationHash || 'SHA256:7f83b1657ff1fc53b92dc18148a1d65dfc2d4b1fa3d677284addd200126d9069'}
                          </p>
                          <span className="text-[7.5px] font-mono text-slate-500 block mt-0.5">
                            Tamper-Evident Digital Ledger • MediCore OS v4.8
                          </span>
                        </div>
                      </div>

                      {/* Direct Inline Print Button for Clinical EHR Note */}
                      <div className="mt-8 pt-4 border-t border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-3 no-print">
                        <div className="text-[11px] text-slate-500 flex items-center gap-1.5">
                          <ShieldCheck className="h-4 w-4 text-emerald-600" />
                          <span>Tamper-evident legal electronic health record ready for clinical or legal archive.</span>
                        </div>
                        <button
                          onClick={() => window.print()}
                          className="w-full sm:w-auto bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-400 hover:to-emerald-400 text-slate-950 font-bold px-5 py-2.5 rounded-xl transition text-xs shadow-lg shadow-teal-500/25 active:scale-95 flex items-center justify-center gap-2"
                        >
                          <Printer className="h-4 w-4" />
                          <span>Print Official Document (1-Click)</span>
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  /* Compact Laminated 3x5 Caregiver Pocket Card */
                  <div
                    id="printable-document"
                    className="w-full max-w-[520px] bg-white text-slate-900 p-6 rounded-2xl shadow-2xl font-sans text-xs border-2 border-slate-900"
                  >
                    <div className="border-b-2 border-slate-900 pb-3 flex items-center justify-between">
                      <div>
                        <span className="text-[9px] font-mono font-bold bg-slate-900 text-white px-2 py-0.5 rounded uppercase tracking-wider">
                          MEDICORE SENIOR LIVING • POCKET CAREGIVER CARD
                        </span>
                        <h2 className="text-xl font-black text-slate-900 mt-1">
                          {selectedResident.fullName}{' '}
                          {selectedResident.preferredName && (
                            <span className="text-teal-700 font-semibold text-base">("{selectedResident.preferredName}")</span>
                          )}
                        </h2>
                        <p className="text-[11px] font-mono text-slate-600 font-bold">
                          ROOM: {selectedResident.roomNumber} • AGE: {selectedResident.age}
                        </p>
                      </div>
                      <div className="text-right">
                        <span className="border-2 border-red-700 text-red-700 px-2 py-1 font-mono font-black text-xs block">
                          {selectedResident.codeStatus}
                        </span>
                      </div>
                    </div>

                    {/* Primary Safety Alerts */}
                    <div className="mt-3 grid grid-cols-2 gap-2 text-[11px]">
                      <div className="p-2.5 bg-red-50 border border-red-300 rounded-xl">
                        <span className="font-black text-red-800 block text-[10px] uppercase">FALL RISK STATUS</span>
                        <span className="font-bold text-red-900 text-xs">{selectedResident.fallRisk}</span>
                        <p className="text-[9px] text-red-700 mt-0.5">Morse Score: {selectedResident.fallRiskScoreMorse} / 125</p>
                      </div>

                      <div className="p-2.5 bg-blue-50 border border-blue-300 rounded-xl">
                        <span className="font-black text-blue-800 block text-[10px] uppercase">TRANSFER PROTOCOL</span>
                        <span className="font-bold text-blue-900 text-xs">{selectedResident.mobilityTransfer}</span>
                        <p className="text-[9px] text-blue-700 mt-0.5">Requires 2-Staff Assist &amp; Walker</p>
                      </div>
                    </div>

                    {/* Dietary Consistency */}
                    <div className="mt-3 p-3 bg-amber-50 border border-amber-300 rounded-xl text-[11px]">
                      <span className="font-black text-amber-900 block text-[10px] uppercase">
                        DIETARY &amp; FLUID CONSISTENCY (ASPIRATION PRECAUTION)
                      </span>
                      <div className="grid grid-cols-2 gap-2 mt-1 font-bold text-slate-900">
                        <div>
                          <span className="text-slate-600 text-[10px] block font-normal">Food Texture:</span>
                          <span>{selectedResident.dietaryTexture}</span>
                        </div>
                        <div>
                          <span className="text-slate-600 text-[10px] block font-normal">Fluid Thickness:</span>
                          <span>{selectedResident.fluidConsistency}</span>
                        </div>
                      </div>
                    </div>

                    {/* Repositioning Schedule */}
                    <div className="mt-3 p-2.5 bg-slate-100 border border-slate-300 rounded-xl text-[11px]">
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="font-black text-slate-800 block text-[10px] uppercase">Q2H REPOSITION PROTOCOL</span>
                          <span className="text-slate-700">Turn interval: Every 2 hours strictly</span>
                        </div>
                        <div className="text-right">
                          <span className="text-[10px] text-slate-500 block">Current Position:</span>
                          <span className="font-bold font-mono text-slate-900">{selectedResident.turnSide}</span>
                        </div>
                      </div>
                    </div>

                    {/* Emergency Contact */}
                    <div className="mt-3 pt-3 border-t border-slate-300 text-[10px] space-y-1">
                      <div>
                        <strong className="text-slate-700">Family Proxy / Emergency Contact: </strong>
                        <span className="text-slate-900">
                          {selectedResident.primaryEmergencyContact?.name || (selectedResident as any).familyContact?.name || 'Sarah Vance'} (
                          {selectedResident.primaryEmergencyContact?.relation || (selectedResident as any).familyContact?.relation || 'Daughter / Proxy'}) •{' '}
                          {selectedResident.primaryEmergencyContact?.phone || (selectedResident as any).familyContact?.phone || '(617) 555-0144'}
                        </span>
                      </div>
                    </div>

                    {/* Direct Inline Print Button for Pocket Card */}
                    <div className="mt-4 pt-3 border-t border-slate-200 flex items-center justify-between no-print">
                      <span className="text-[10px] font-mono text-slate-500">
                        Standard 3x5 Shift Lanyard Badge
                      </span>
                      <button
                        onClick={() => window.print()}
                        className="bg-indigo-600 hover:bg-indigo-500 text-white font-bold px-4 py-2 rounded-xl text-xs transition flex items-center gap-1.5 shadow-md shadow-indigo-600/20 active:scale-95"
                      >
                        <Printer className="h-3.5 w-3.5" />
                        <span>Print Official Document</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </main>
      </div>

      {/* Official Print Modal with Dual EHR & Pocket Card Support */}
      <PrintPreviewModal
        document={currentDoc}
        patient={selectedPatient}
        isOpen={isPrintModalOpen}
        onClose={() => setIsPrintModalOpen(false)}
        pocketResident={selectedResident}
        initialMode={printModalMode}
        currentBusiness={currentBusiness}
      />

      {/* AI Family Digest SMS Modal */}
      <FamilyDigestModal
        subject={familyDigestSubject}
        isOpen={isFamilyDigestOpen}
        onClose={() => setIsFamilyDigestOpen(false)}
        onSendSMS={handleSendFamilySMS}
      />

      {/* Access Management & Multi-Business License Modal */}
      <AccessManagementModal
        isOpen={isAccessModalOpen}
        onClose={() => setIsAccessModalOpen(false)}
        businesses={businesses}
        users={users}
        currentBusiness={currentBusiness}
        currentUser={currentUser}
        onSelectBusiness={(biz) => {
          setCurrentBusiness(biz);
          setOrderToast(`Switched active clinical workspace to ${biz.name}`);
          setTimeout(() => setOrderToast(null), 3500);
        }}
        onSelectUser={(u) => {
          setCurrentUser(u);
          setOrderToast(`Switched active clinician to ${u.fullName} (${u.role.replace(/_/g, ' ')})`);
          setTimeout(() => setOrderToast(null), 3500);
        }}
        onUpgradeLicense={handleUpgradeLicense}
      />

      {/* Floating MediCore AI Clinical Assistant */}
      <AiClinicalAssistant
        patients={patients}
        onSelectPatient={(p) => {
          setSelectedPatient(p);
          setCurrentTab('patients');
        }}
        onOpenDocs={() => {
          setCurrentTab('clinicalDocs');
        }}
      />
    </div>
  );
};

export default App;
